/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

// Continuous 1-Row Infinite Partner Slider Component
const PartnerMarquee = ({ items, reverse = false, speed = 25 }) => {
  if (!items || items.length === 0) {
    return (
      <div className="text-gray-400 text-center py-6">
        No partners available at the moment.
      </div>
    );
  }

  // Ensure enough items to fill and loop seamlessly
  const minCount = 8;
  const factor = Math.max(1, Math.ceil(minCount / items.length));
  let baseList = [];
  for (let i = 0; i < factor; i++) {
    baseList = [...baseList, ...items];
  }
  // Double list for seamless 50% translation loop
  const displayList = [...baseList, ...baseList];

  return (
    <div className="relative w-full overflow-hidden py-4">
      {/* Subtle fade edges for smooth transition */}
      <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-28 bg-gradient-to-r from-gray-800 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-28 bg-gradient-to-l from-gray-800 to-transparent z-10 pointer-events-none" />

      <motion.div
        className="flex gap-6 sm:gap-8 w-max items-center"
        animate={{
          x: reverse ? ["-50%", "0%"] : ["0%", "-50%"],
        }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: speed,
            ease: "linear",
          },
        }}
      >
        {displayList.map((item, index) => (
          <motion.a
            key={`${item.id || item._id || 'item'}-${index}`}
            href={item.website || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex flex-col items-center justify-center p-4 bg-gray-900/80 hover:bg-gray-900 border border-gray-700/60 hover:border-yellow-500/60 rounded-xl w-44 sm:w-56 h-28 sm:h-32 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-yellow-500/10 group"
            whileHover={{ scale: 1.05 }}
          >
            <div className="h-12 sm:h-14 flex items-center justify-center w-full px-2">
              <img
                src={item.logoUrl}
                alt={item.name}
                className="max-h-full max-w-full object-contain filter group-hover:brightness-110 transition-all duration-300"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/64?text=Logo";
                }}
              />
            </div>
            <p className="text-gray-200 group-hover:text-yellow-400 font-semibold text-xs sm:text-sm mt-2 text-center truncate w-full px-1 transition-colors duration-300">
              {item.name}
            </p>
          </motion.a>
        ))}
      </motion.div>
    </div>
  );
};

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API URLs
  const PARTNERS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIecosystemPartners`;
  const BANKS_API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIbanksPartners`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;

  // Fetch partners data with image loading
  const fetchPartners = async () => {
    try {
      const response = await axios.get(PARTNERS_API_URL);
      const data = response.data.results || response.data;

      // Load images for each partner
      const partnersWithImages = await Promise.all(
        data.map(async (partner) => {
          let logoUrl = "";
          
          if (partner.logo?.id) {
            try {
              const imgRes = await axios.get(`${API_MEDIA}/${partner.logo.id}/download`, {
                responseType: "blob",
              });
              logoUrl = URL.createObjectURL(imgRes.data);
            } catch (error) {
              console.error("Error loading partner logo:", error);
              logoUrl = "https://via.placeholder.com/64?text=Logo";
            }
          } else if (typeof partner.logo === 'string' && partner.logo.startsWith('http')) {
            logoUrl = partner.logo;
          } else {
            logoUrl = "https://via.placeholder.com/64?text=Logo";
          }

          return {
            ...partner,
            logoUrl,
            website: partner.website || "#"
          };
        })
      );

      return partnersWithImages;
    } catch (error) {
      console.error("Error fetching partners:", error);
      throw error;
    }
  };

  // Fetch banks data with image loading
  const fetchBanks = async () => {
    try {
      const response = await axios.get(BANKS_API_URL);
      const data = response.data.results || response.data;

      // Load images for each bank
      const banksWithImages = await Promise.all(
        data.map(async (bank) => {
          let logoUrl = "";
          
          if (bank.logo?.id) {
            try {
              const imgRes = await axios.get(`${API_MEDIA}/${bank.logo.id}/download`, {
                responseType: "blob",
              });
              logoUrl = URL.createObjectURL(imgRes.data);
            } catch (error) {
              console.error("Error loading bank logo:", error);
              logoUrl = "https://via.placeholder.com/64?text=Logo";
            }
          } else if (typeof bank.logo === 'string' && bank.logo.startsWith('http')) {
            logoUrl = bank.logo;
          } else {
            logoUrl = "https://via.placeholder.com/64?text=Logo";
          }

          return {
            ...bank,
            logoUrl,
            website: bank.website || "#"
          };
        })
      );

      return banksWithImages;
    } catch (error) {
      console.error("Error fetching banks:", error);
      throw error;
    }
  };

  // Fetch all data
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [partnersData, banksData] = await Promise.all([
          fetchPartners(),
          fetchBanks()
        ]);

        const activePartners = partnersData.filter(partner => partner.isActive !== false);
        const activeBanks = banksData.filter(bank => bank.isActive !== false);

        setPartners(activePartners);
        setBanks(activeBanks);
      } catch (err) {
        setError("Failed to load partners data. Please try again later.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      [...partners, ...banks].forEach(item => {
        if (item.logoUrl && item.logoUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.logoUrl);
        }
      });
    };
  }, [partners, banks]);

  // Gradient text style
  const gradientText = {
    background: "linear-gradient(90deg, #eab308, #ffffff, #eab308)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
  };

  // Loading component
  if (loading) {
    return (
      <section className="bg-gray-800 py-16">
        <div className="container mx-auto text-center">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          </div>
          <p className="text-gray-300 mt-4">Loading partners...</p>
        </div>
      </section>
    );
  }

  // Error component
  if (error) {
    return (
      <section className="bg-gray-800 py-16">
        <div className="container mx-auto text-center">
          <p className="text-red-500 text-lg">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Ecosystem Partners Section */}
      <section className="bg-gray-800 py-10 sm:py-14 relative overflow-hidden">
        <div className="w-full text-center relative z-10">
          <motion.h2
            className="text-2xl sm:text-4xl font-bold"
            style={gradientText}
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Ecosystem Partners
          </motion.h2>
          <p className="text-gray-300 mt-2 max-w-lg mx-auto text-sm sm:text-base px-4 mb-6">
            We proudly collaborate with industry leaders to deliver excellence.
          </p>

          {/* Continuous 1-Row Infinite Slider */}
          <PartnerMarquee items={partners} reverse={false} speed={30} />
        </div>
      </section>

      {/* Bank & Exchange Partners Section */}
      <section className="bg-gray-800 py-10 sm:py-14 relative overflow-hidden">
        <div className="w-full text-center relative z-10">
          <motion.h2
            className="text-2xl sm:text-4xl font-bold"
            style={gradientText}
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Bank & Exchange Partners
          </motion.h2>
          <p className="text-gray-300 mt-2 max-w-lg mx-auto text-sm sm:text-base px-4 mb-6">
            We proudly collaborate with industry leaders to deliver excellence.
          </p>

          {/* Continuous 1-Row Infinite Slider */}
          <PartnerMarquee items={banks} reverse={true} speed={30} />
        </div>
      </section>
    </>
  );
};

export default Partners;